import React, { useEffect, useState, useMemo } from "react";
import { Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Box  } from '@mui/material';
import ChampPicksTeamCard from "./ChampPicksTeamCard/ChampPicksTeamCard";
import PercentagePillBoxWithText from "../../GamePageComponents/PercentagePillBox/PercentagePillBoxWithText";
import { useTheme } from "@emotion/react";
import { useMediaQuery } from "@mui/material";
import { DateTime } from 'luxon';
import { useUserAndGameContext } from "../../../contexts/UserAndGameContext";
import { useSuperPickemContext } from "../context/SuperPickemContext";
import { getTeamLogo, getTeamName } from "../../../utils/SportsData";
import QuickPickGamesComing from "../../QuickPickGames/CardPieces/QuickPickGamesComing";

const ChampPicksNew = ({
    
}) => {
    
    const {
        leaderboardData,
        league,
        masterPoolData,
    } = useUserAndGameContext();

    const {
        teams,
        hasBracketDeadlinePassed,
    } = useSuperPickemContext();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const poolPicksModalInfo = masterPoolData?.modalInfo?.poolPicks;

     // State to manage sorting
     const [sortConfig, setSortConfig] = useState({
        key: 'count', // Default sort by count
        direction: 'descending' // Default to descending order
    });

       // Use Effect To Sort by Seed originally if !hasBracketDeadlinePassed
       useEffect(() => {
        //console.log("Setting Sort Config");
        if (!hasBracketDeadlinePassed) {
            setSortConfig({
                key: 'seed',
                direction: 'ascending',
            });
        }
    }, [hasBracketDeadlinePassed]);

// Optimized championship data processor
const processChampionshipData = (leaderboardData) => {
    // Use object for O(1) lookup instead of array.find()
    const teamsMap = {};
    
    const champData = {
      tableHeader: `${league} Championship Picks`,
      totalPicks: 0,
      Games: []
    };
  
    leaderboardData?.forEach(user => {
      const userChampPick = user?.bracketPicks?.find(pick => pick.round === 4);
      
      if (userChampPick) {
        // Increment total picks count
        champData.totalPicks += 1;
        
        const teamId = userChampPick.teamId;
        
        if (teamsMap[teamId]) {
          // If team already exists, just increment the pick count
          teamsMap[teamId].pickCount += 1;
        } else {
          // Create a new team entry
          const newTeam = {
            seed: userChampPick.teamSeed,
            teamId: teamId,
            pickCount: 1,
            result: userChampPick.result || "pending"
          };
          
          // Store in map and push to array
          teamsMap[teamId] = newTeam;
          champData.Games.push(newTeam);
        }
      }
    });
  
    // Sort by pickCount (most popular first)
    champData.Games.sort((a, b) => b.pickCount - a.pickCount);
    
    return champData;
  };


  const champStats = processChampionshipData(leaderboardData);
    const totalPickCount = champStats?.totalPicks;
    const champStatsArray = champStats?.Games;

    // Memoized sorted array that updates based on sortConfig
    // Updated sorting function to handle both numeric and string seeds
    const sortedChampStats = useMemo(() => {
        if (!champStatsArray) return [];

        return [...champStatsArray].sort((a, b) => {
            if (sortConfig.key === 'seed') {
                // Handle both numeric and string-based seeds (like NHL's "WC1", "A2")
                const seedA = a.seed;
                const seedB = b.seed;
                
                // If both seeds are numbers, do numeric comparison
                if (typeof seedA === 'number' && typeof seedB === 'number') {
                    return sortConfig.direction === 'ascending' 
                        ? (seedA - seedB) 
                        : (seedB - seedA);
                }
                
                // If one is a number and one is a string, numbers come first
                if (typeof seedA === 'number' && typeof seedB !== 'number') {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (typeof seedA !== 'number' && typeof seedB === 'number') {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                
                // If both are strings, compare alphabetically
                // This handles NHL-style seeds like "WC1" vs "A2"
                return sortConfig.direction === 'ascending'
                    ? String(seedA).localeCompare(String(seedB))
                    : String(seedB).localeCompare(String(seedA));
            } else {
                // For count, sort by pickCount
                return sortConfig.direction === 'ascending'
                    ? (a.pickCount - b.pickCount)
                    : (b.pickCount - a.pickCount);
            }
        });
    }, [champStatsArray, sortConfig]);
    
    // Handle sorting toggle
    const handleSort = (key) => {
        setSortConfig(prevConfig => {
            // If sorting the same key, toggle direction
            if (prevConfig.key === key) {
                return {
                    key,
                    direction: prevConfig.direction === 'ascending' ? 'descending' : 'ascending'
                };
            }
            // If new key, default to descending for count, ascending for seed
            return {
                key,
                direction: key === 'count' ? 'descending' : 'ascending'
            };
        });
    };


    const formatDate = (dateString) => {
        const date = DateTime.fromISO(dateString, { zone: 'America/New_York' }); // Use ET timezone
        return date.toFormat("EEE M/d '@' h:mma 'ET'");
    };

  return (
        <>
            {!hasBracketDeadlinePassed && (
                <>
                    <QuickPickGamesComing
                        page={'pool-picks'}
                        title={poolPicksModalInfo?.title}
                        body={poolPicksModalInfo?.body}
                        quickPick={false}
                    />

                    <Box sx={{ marginBottom: '48px' }} /> 
                </>
            )}

            {hasBracketDeadlinePassed && (
                <TableContainer 
                    component={Paper}
                    sx={{
                        maxWidth: '700px',
                        margin: '0 auto',
                        borderRadius: '0px',
                        marginTop: '24px',
                    }}    
                >
                    <Table>
                        <TableHead>
                            <TableRow
                                sx={{
                                    backgroundColor: '#002129',
                                    height: '56px',
                                }}
                            >
                                <TableCell
                                    sx={{
                                        width: '51%',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => handleSort('seed')}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: '14px',
                                            fontWeight: '700',
                                            textAlign: 'center',
                                            textDecoration: 'underline',
                                            color: sortConfig.key === 'seed' ? '#FFC60A' : '#fff',
                                        }}
                                    >
                                        CHAMPION
                                    </Typography>
                                </TableCell>
                                <TableCell
                                    sx={{
                                        width: '49%',
                                        justifyContent: 'center',
                                        alignContent: 'center',
                                        cursor: hasBracketDeadlinePassed ? 'pointer' : 'default', // Change cursor style
                                    }}
                                    onClick={() => {
                                        if (hasBracketDeadlinePassed) {
                                            handleSort('count'); // Only call if condition is true
                                        }
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: '14px',
                                            fontWeight: '700',
                                            textAlign: 'center',
                                            textDecoration: hasBracketDeadlinePassed ? 'underline' : 'none',
                                            color: sortConfig.key === 'count' ? '#FFC60A' : '#fff',
                                        }}
                                    >
                                        TIMES PICKED
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedChampStats?.map((champStat, index) => {
                                const teamId = champStat?.teamId;
                                const result = champStat?.result;
                                const count = champStat?.pickCount;
                                // console.log("Count: ", count, "Champ Stat: ", champStat);
                                const seed = champStat?.seed;
                                const teamLogo = getTeamLogo(league, teamId, teams);
                                const teamName = getTeamName(league, teamId, teams);

                                return (
                                    <TableRow
                                        key={index}
                                        sx={{
                                            backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F4F4F4',
                                        }}
                                    >
                                        <TableCell
                                            sx={{
                                                padding: '16px 0px 16px 0px',
                                            }}
                                        >
                                            <ChampPicksTeamCard
                                                teamInfo={{
                                                    seed: seed,
                                                    nameDisplay: teamName,
                                                    teamDisplayCharacterCount: teamName?.length,
                                                    logo: teamLogo,
                                                    teamColor: '#002129',
                                                }}
                                                result={result}
                                                areSeedsIn={true}
                                                index={index}
                                            />
                                        </TableCell>
                                        {hasBracketDeadlinePassed ? (
                                            <TableCell
                                                sx={{
                                                    padding: '16px 0px 16px 0px',
                                                }}
                                            >
                                                <PercentagePillBoxWithText
                                                    count={count}
                                                    total={totalPickCount}
                                                    result={result}
                                                />
                                            </TableCell>
                                            ) : (
                                                <TableCell
                                                    sx={{
                                                        padding: '16px 0px 16px 0px',
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: '90%',
                                                            border: '1px solid #002129',
                                                            borderRadius: '8px',
                                                            margin: '0 auto',
                                                            height: '40px',
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontSize: '14px',
                                                                fontWeight: '700',
                                                                color: '#002129',
                                                                textAlign: 'center',
                                                            }}
                                                        >
                                                            TBD
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </>
    );
};

export default ChampPicksNew;